// Launch sequence (30 Sep 2026): plays once right after a successful sign-in, in the same
// instrument language as the landing intro. White stage; a dot grid, rings, degree scale,
// reticle arcs and a radar sweep draw around the BIIC mark; system checks tick off on the
// left while a boot gauge fills on the right; "Welcome, <name>" and the portal land under
// the mark; then the six sash stripes sweep across and the overlay clears onto the app.
// Click, Esc or any key skips. Reduced motion: never shown.
//
// Trigger: LoginPage sets sessionStorage `knv-launch` = '1' on success; AppShell mounts this
// component, which consumes the flag once.

import { useEffect, useRef, useState } from 'react';
import { animate, createTimeline, stagger, svg, utils } from 'animejs';
import { BiicMark } from './BiicLogo';
import { useAuthStore } from '../../lib/auth/AuthContext';

const FLAG = 'knv-launch';
const DISPLAY = "Georgia, 'Times New Roman', serif";
const MONO = "'JetBrains Mono', ui-monospace, monospace";
const INK = '#0B3D2E';
const HUD = 'rgba(11,61,46,0.55)';
const STRIPES = ['#007A4D', '#FFB612', '#000000', '#FFFFFF', '#DE3831', '#001489'];
const VW = 1000, VH = 600, CX = 500, CY = 300;

export function markLaunchPending() {
  try { sessionStorage.setItem(FLAG, '1'); } catch { /* storage blocked: no intro */ }
}

function consumeFlag(): boolean {
  try {
    if (sessionStorage.getItem(FLAG) !== '1') return false;
    sessionStorage.removeItem(FLAG);
    return true;
  } catch { return false; }
}

const reduced = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

const polar = (r: number, deg: number) => [CX + r * Math.cos((deg * Math.PI) / 180), CY + r * Math.sin((deg * Math.PI) / 180)];
const arc = (r: number, a0: number, a1: number) => {
  const [x0, y0] = polar(r, a0), [x1, y1] = polar(r, a1);
  return `M${x0.toFixed(1)} ${y0.toFixed(1)} A${r} ${r} 0 ${a1 - a0 > 180 ? 1 : 0} 1 ${x1.toFixed(1)} ${y1.toFixed(1)}`;
};

const PORTAL: Record<string, string> = {
  admin: 'Admin', exec: 'Executive', governance: 'Compliance', investigator: 'Investigator', biic_investigator: 'BIIC Investigator',
  biic_analyst: 'CIA Analyst', biic_case_manager: 'Case Manager', reporter: 'Reporter', sars_official: 'Revenue Liaison',
};

/** Mounted once at the app root, so route changes and shell remounts after sign-in cannot cut it short. */
export function LaunchSequence() {
  const user = useAuthStore(s => s.user);
  const workspace = useAuthStore(s => s.workspace);
  const name = user?.name;
  const portal = workspace?.eyebrow || (user?.role ? PORTAL[user.role] ?? null : null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (user && !reduced() && consumeFlag()) setShow(true);
  }, [user]);
  const root = useRef<HTMLDivElement>(null);
  const first = (name ?? '').trim().split(/\s+/)[0] || '';

  const checks = [
    ['Identity', 'verified'],
    ['Workspace', (portal || 'BIIC').toLowerCase()],
    ['Product registry', 'synced'],
    ['Golden images', 'loaded'],
    ['Case ledger', 'hash chain ok'],
    ['Field network', '9 provinces online'],
  ];

  useEffect(() => {
    if (!show) return;
    const el = root.current;
    if (!el) return;
    const q = <T extends Element>(s: string) => el.querySelector<T>(s)!;
    const qa = <T extends Element>(s: string) => Array.from(el.querySelectorAll<T>(s));
    let done = false;
    const loops: ReturnType<typeof animate>[] = [];
    const finish = () => {
      if (done) return;
      done = true;
      tl.pause();
      animate(el, { opacity: [Number(getComputedStyle(el).opacity) || 1, 0], duration: 260, ease: 'outQuad', onComplete: () => setShow(false) });
    };

    const rings = svg.createDrawable(qa<SVGElement>('.lx-ring'));
    const hairs = svg.createDrawable(qa<SVGLineElement>('.lx-hair'), 0.5, 0.5);
    utils.set(qa('.lx-fade'), { opacity: 0 });
    utils.set(qa('.lx-check'), { opacity: 0, translateX: -10 });
    utils.set(qa('.lx-ok'), { opacity: 0, scale: 0.4 });
    utils.set(q('.lx-mark'), { opacity: 0, scale: 0.9 });
    utils.set(q('.lx-welcome'), { opacity: 0, translateY: 14 });
    utils.set(q('.lx-portal'), { opacity: 0, translateY: 8 });
    utils.set(q('.lx-fill'), { scaleX: 0 });
    utils.set(qa('.lx-wipe'), { translateX: '-130%' });
    utils.set(q('.lx-scan'), { opacity: 0, translateY: 0 });

    loops.push(animate(q('.lx-reticle'), { rotateZ: 360, duration: 22000, ease: 'linear', loop: true }));
    loops.push(animate(q('.lx-ring-dash'), { rotateZ: -360, duration: 40000, ease: 'linear', loop: true }));
    loops.push(animate(q('.lx-sweep'), { rotateZ: 360, duration: 3200, ease: 'linear', loop: true }));
    loops.push(animate(qa('.lx-bar'), { scaleY: () => utils.random(0.25, 1.7, 2), duration: () => utils.random(220, 600), ease: 'inOutSine', loop: true, alternate: true, delay: stagger(20) }));

    const pct = q<HTMLSpanElement>('.lx-pct');
    const gauge = { v: 0 };
    const tl = createTimeline({ defaults: { ease: 'outCubic' }, onComplete: () => { done = true; setShow(false); } });
    tl.add(q('.lx-grid'), { opacity: [0, 0.6], duration: 900 }, 0)
      .add(rings, { draw: ['0 0', '0 1'], duration: 1100, ease: 'inOutSine', delay: stagger(130) }, 100)
      .add(hairs, { draw: ['0.5 0.5', '0 1'], duration: 900, ease: 'inOutSine' }, 200)
      .add(qa('.lx-frame'), { opacity: [0, 1], duration: 300, delay: stagger(60) }, 150)
      .add(qa('.lx-deg'), { opacity: [0, 1], duration: 140, delay: stagger(4) }, 300)
      .add(qa('.lx-deg-label'), { opacity: [0, 1], duration: 300, delay: stagger(80) }, 700)
      .add(q('.lx-reticle'), { opacity: [0, 1], duration: 400 }, 450)
      .add(q('.lx-ring-dash'), { opacity: [0, 1], duration: 500 }, 350)
      .add(q('.lx-sweep'), { opacity: [0, 1], duration: 400 }, 500)
      .add(qa('.lx-cross'), { opacity: [0, 1], duration: 260, delay: stagger(40) }, 650)
      .add(qa('.lx-bar'), { opacity: [0, 1], duration: 200, delay: stagger(10) }, 700)
      .add(q('.lx-mark'), { opacity: [0, 1], scale: [0.9, 1], duration: 900, ease: 'outExpo' }, 700)
      .add(q('.lx-scan'), { opacity: [0, 1, 1, 0], translateY: [-150, 150], duration: 1100, ease: 'inOutSine' }, 1000)
      .add(qa('.lx-panel'), { opacity: [0, 1], duration: 400, delay: stagger(120) }, 900);
    qa('.lx-check').forEach((c, i) => {
      tl.add(c, { opacity: [0, 1], translateX: [-10, 0], duration: 280 }, 1100 + i * 260)
        .add(qa('.lx-ok')[i], { opacity: [0, 1], scale: [0.4, 1], duration: 240, ease: 'outBack' }, 1300 + i * 260);
    });
    tl.add(q('.lx-fill'), { scaleX: [0, 1], duration: 1700, ease: 'inOutCubic' }, 1100)
      .add(gauge, { v: [0, 100], duration: 1700, ease: 'inOutCubic', onUpdate: () => { pct.textContent = String(Math.round(gauge.v)).padStart(3, '0'); } }, 1100)
      .add(q('.lx-welcome'), { opacity: [0, 1], translateY: [14, 0], duration: 700 }, 2500)
      .add(q('.lx-portal'), { opacity: [0, 1], translateY: [8, 0], duration: 500 }, 2750)
      .add(qa('.lx-wipe'), { translateX: ['-130%', '130%'], duration: 900, ease: 'inOutQuart', delay: stagger(55) }, 3600)
      .add(q('.lx-stage'), { opacity: [1, 0], duration: 200, ease: 'linear' }, 3900)
      .add(el, { opacity: [1, 0], duration: 350, ease: 'outQuad' }, 4200);

    const onKey = (e: KeyboardEvent) => { if (e.key) finish(); };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); tl.revert(); loops.forEach(l => l.revert()); };
  }, [show]);

  if (!show) return null;

  const R1 = 150, R2 = 176, R3 = 204, R4 = 232;
  return (
    <div ref={root} role="presentation" aria-hidden
      style={{ position: 'fixed', inset: 0, zIndex: 5000, background: '#fff', overflow: 'hidden', cursor: 'pointer' }}
      onPointerDown={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))}>
      <style>{`
        .lx-side { position: absolute; top: 50%; transform: translateY(-50%); width: min(300px, 24vw); }
        .lx-left { left: clamp(20px, 4vw, 64px); }
        .lx-right { right: clamp(20px, 4vw, 64px); }
        .lx-hello { top: calc(50% + min(26vh, 200px)); }
        @media (max-width: 820px) {
          .lx-side { position: absolute; top: auto; transform: none; width: auto; left: 20px; right: 20px; }
          .lx-left { bottom: 20px; font-size: 10.5px !important; }
          .lx-left .lx-check { padding: 3px 0 !important; }
          .lx-right { display: none; }
          .lx-hello { top: calc(50% + 70px); }
          .lx-markwrap { transform: scale(.72); }
          .lx-bar { display: none; }
        }
      `}</style>
      <div className="lx-stage" style={{ position: 'absolute', inset: 0 }}>
        <svg viewBox={`0 0 ${VW} ${VH}`} width="100%" height="100%" preserveAspectRatio="xMidYMid slice" style={{ position: 'absolute', inset: 0 }}>
          <defs>
            <pattern id="lxdots" width="32" height="32" patternUnits="userSpaceOnUse"><circle cx="16" cy="16" r="0.7" fill={HUD} /></pattern>
          </defs>
          <rect className="lx-grid" x="-200" y="-200" width={VW + 400} height={VH + 400} fill="url(#lxdots)" opacity="0" />
          <g fill="none" stroke={HUD} strokeLinecap="square">
            <circle className="lx-ring" cx={CX} cy={CY} r={R1} strokeWidth="0.9" />
            <circle className="lx-ring" cx={CX} cy={CY} r={R3} strokeWidth="0.6" />
            <path className="lx-ring" d={arc(R4 + 18, -160, 110)} strokeWidth="1.2" />
            <circle className="lx-ring-dash lx-fade" cx={CX} cy={CY} r={R2} strokeWidth="0.6" strokeDasharray="1.5 5" style={{ transformBox: 'view-box', transformOrigin: `${CX}px ${CY}px`, opacity: 1 }} />
            <line className="lx-hair" x1={CX - 480} y1={CY} x2={CX + 480} y2={CY} strokeWidth="0.5" />
            <line className="lx-hair" x1={CX} y1={CY - 290} x2={CX} y2={CY + 290} strokeWidth="0.5" />
            {Array.from({ length: 72 }, (_, i) => {
              const a = i * 5, long = i % 6 === 0;
              const [x1, y1] = polar(R4 - (long ? 10 : 4), a), [x2, y2] = polar(R4, a);
              return <line key={i} className="lx-deg lx-fade" x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth={long ? 1 : 0.5} />;
            })}
            {[['000', -90], ['180', 90]].map(([t, a]) => {
              const [x, y] = polar(R4 + 16, a as number);
              return <text key={t} className="lx-deg-label lx-fade" x={x} y={y + 3} textAnchor="middle" fontFamily={MONO} fontSize="8" fill={HUD} stroke="none">{t}</text>;
            })}
            <g className="lx-reticle lx-fade" style={{ transformBox: 'view-box', transformOrigin: `${CX}px ${CY}px` }}>
              <path d={arc(R1 + 12, -110, -60)} strokeWidth="1.8" />
              <path d={arc(R1 + 12, 70, 120)} strokeWidth="1.8" />
              <path d={arc(R1 + 12, 160, 178)} strokeWidth="0.8" />
              <path d={arc(R1 + 12, -20, -2)} strokeWidth="0.8" />
            </g>
            <g className="lx-sweep lx-fade" style={{ transformBox: 'view-box', transformOrigin: `${CX}px ${CY}px` }}>
              <path d={`M${CX} ${CY} L${CX} ${CY - R1} ${arc(R1, -90, -40).replace(/^M[^A]+/, '')} Z`} fill="rgba(11,61,46,0.05)" stroke="none" />
              <line x1={CX} y1={CY} x2={CX} y2={CY - R1} strokeWidth="0.7" />
            </g>
            {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy], i) => {
              const x = CX + sx * 470, y = CY + sy * 270, t = 22;
              return (
                <g key={i} className="lx-frame lx-fade">
                  <path d={`M${x - sx * t} ${y} L${x} ${y} L${x} ${y - sy * t}`} strokeWidth="1" />
                  {[0, 1, 2].map(k => <line key={k} x1={x - sx * (t + 8 + k * 5)} y1={y} x2={x - sx * (t + 8 + k * 5)} y2={y - sy * (k === 1 ? 5 : 3)} strokeWidth="0.7" />)}
                </g>
              );
            })}
            {Array.from({ length: 8 }, (_, i) => {
              const [x, y] = polar(290, i * 45 + 22.5);
              return <path key={i} className="lx-cross lx-fade" d={`M${x - 4} ${y} L${x + 4} ${y} M${x} ${y - 4} L${x} ${y + 4}`} strokeWidth="0.8" />;
            })}
            <line className="lx-scan" x1={CX - 200} y1={CY} x2={CX + 200} y2={CY} strokeWidth="0.9" stroke={INK} />
          </g>
          {Array.from({ length: 28 }, (_, i) => {
            const h = 3 + ((i * 7) % 10);
            return <rect key={i} className="lx-bar lx-fade" x={CX - 70 + i * 5} y={CY + 128 - h} width="2.4" height={h} fill={HUD} style={{ transformBox: 'fill-box', transformOrigin: 'center bottom' }} />;
          })}
        </svg>

        {/* Mark, welcome and portal, centred. */}
        <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', pointerEvents: 'none' }}>
          <div className="lx-markwrap" style={{ marginTop: -40 }}><div className="lx-mark"><BiicMark size={132} /></div></div>
        </div>
        <div className="lx-hello" style={{ position: 'absolute', left: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, pointerEvents: 'none', padding: '0 20px', textAlign: 'center' }}>
          <div className="lx-welcome" style={{ fontFamily: DISPLAY, fontWeight: 700, fontSize: 'clamp(26px, 3.6vw, 44px)', letterSpacing: '-0.02em', color: '#16211b' }}>
            {first ? `Welcome, ${first}.` : 'Welcome.'}
          </div>
          <div className="lx-portal" style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#4d5a52' }}>
            Brand Integrity Intelligence Centre{portal ? ` · ${portal}` : ''}
          </div>
        </div>

        {/* Left: system checks. */}
        <div className="lx-side lx-left lx-panel lx-fade" style={{ fontFamily: MONO, fontSize: 11.5, color: '#1f2a24' }}>
          <div style={{ letterSpacing: '0.14em', textTransform: 'uppercase', color: '#66736b', fontSize: 10.5, borderBottom: '1px solid rgba(11,61,46,.25)', paddingBottom: 8, marginBottom: 10 }}>System check</div>
          {checks.map(([k, v]) => (
            <div key={k} className="lx-check" style={{ display: 'grid', gridTemplateColumns: '14px 1fr auto', gap: 10, alignItems: 'center', padding: '5px 0', borderBottom: '1px dashed rgba(11,61,46,.12)' }}>
              <span className="lx-ok" style={{ width: 10, height: 10, background: '#007A4D', display: 'inline-block' }} />
              <span style={{ letterSpacing: '0.04em' }}>{k}</span>
              <span style={{ color: '#66736b' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Right: boot gauge. */}
        <div className="lx-side lx-right lx-panel lx-fade" style={{ fontFamily: MONO, color: '#1f2a24' }}>
          <div style={{ letterSpacing: '0.14em', textTransform: 'uppercase', color: '#66736b', fontSize: 10.5, borderBottom: '1px solid rgba(11,61,46,.25)', paddingBottom: 8, marginBottom: 14 }}>Initialising</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span className="lx-pct" style={{ fontSize: 44, fontWeight: 400, letterSpacing: '-0.02em', color: INK }}>000</span>
            <span style={{ fontSize: 14, color: '#66736b' }}>%</span>
          </div>
          <div style={{ height: 4, background: '#e4e9e3', marginTop: 12, overflow: 'hidden' }}>
            <div className="lx-fill" style={{ height: '100%', background: INK, transformOrigin: 'left center' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 10, color: '#8f9a93', letterSpacing: '0.08em' }}>
            <span>SEC · TLS 1.3</span><span>SHA-256</span><span>POPIA</span>
          </div>
        </div>
      </div>

      {/* Exit wipe: the six sash stripes sweep across. */}
      <div style={{ position: 'absolute', inset: '-20% -10%', transform: 'rotate(-12deg)', display: 'flex', flexDirection: 'column', pointerEvents: 'none' }}>
        {STRIPES.map(c => <div key={c} className="lx-wipe" style={{ flex: 1, background: c, borderTop: c === '#FFFFFF' ? '1px solid rgba(0,0,0,.06)' : 'none' }} />)}
      </div>
    </div>
  );
}
